import { supabase } from '../config/supabase';
import { BaseService, AuthError } from './BaseService';
import { AuthUser } from '../types';

export interface SignUpData {
  email: string;
  password: string;
  fullName?: string;
}

export interface SignInData {
  email: string;
  password: string;
}

export interface AuthResponse {
  user: AuthUser | null;
  session: any | null;
}

export interface PasswordUpdateData {
  oldPassword: string;
  newPassword: string;
}



class AuthService extends BaseService {
  /**
   * Sign up a new user
   */
  async signUp(data: SignUpData): Promise<AuthResponse> {
    try {
      this.validateRequired(data.email, 'Email');
      this.validateRequired(data.password, 'Password');
      this.validateEmail(data.email);

      if (data.password.length < 6) {
        throw new AuthError('Password must be at least 6 characters long');
      }

      console.log('Attempting signup with:', { email: data.email, hasPassword: !!data.password });

      const { data: authData, error } = await supabase.auth.signUp({
        email: data.email,
        password: data.password,
        options: {
          emailRedirectTo: `${window.location.origin}/dashboard`,
          data: {
            full_name: data.fullName || '',
          },
        },
      });

      console.log('Supabase signup response:', { authData, error });

      if (error) {
        console.error('Supabase signup error:', error);
        throw error;
      }

      if (authData.user && (!authData.user.identities || authData.user.identities.length === 0)) {
        throw new AuthError('An account with this email address already exists.');
      }

      // Don't create user settings or assign credits until email is verified
      // This will be handled in the auth state change listener

      return {
        user: authData.user ? {
          id: authData.user.id,
          email: authData.user.email!,
          name: data.fullName || authData.user.user_metadata?.full_name || authData.user.email!.split('@')[0],
          user_metadata: authData.user.user_metadata,
        } : null,
        session: authData.session,
      };
    } catch (error) {
      console.error('AuthService.signUp error:', error);
      this.handleError(error, 'AuthService.signUp');
    }
  }

  /**
   * Sign in existing user
   */
  async signIn(data: SignInData): Promise<AuthResponse> {
    try {
      this.validateRequired(data.email, 'Email');
      this.validateRequired(data.password, 'Password');
      this.validateEmail(data.email);

      console.log('Attempting signin with:', { email: data.email, hasPassword: !!data.password });

      const { data: authData, error } = await supabase.auth.signInWithPassword({
        email: data.email,
        password: data.password,
      });

      console.log('Supabase signin response:', { authData, error });

      if (error) {
        console.error('Supabase signin error:', error);
        throw error;
      }

      return {
        user: authData.user ? {
          id: authData.user.id,
          email: authData.user.email!,
          name: authData.user.user_metadata?.full_name || authData.user.email!.split('@')[0],
          user_metadata: authData.user.user_metadata,
        } : null,
        session: authData.session,
      };
    } catch (error) {
      console.error('AuthService.signIn error:', error);
      this.handleError(error, 'AuthService.signIn');
    }
  }

  /**
   * Sign out current user
   */
  async signOut(): Promise<void> {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) {
        throw error;
      }
    } catch (error) {
      this.handleError(error, 'AuthService.signOut');
    }
  }

  /**
   * Reset password
   */
  async resetPassword(email: string): Promise<void> {
    try {
      this.validateRequired(email, 'Email');
      this.validateEmail(email);

      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) {
        throw error;
      }
    } catch (error) {
      this.handleError(error, 'AuthService.resetPassword');
    }
  }

  /**
   * Update password
   */
  async updatePassword(newPassword: string): Promise<void> {
    try {
      this.validateRequired(newPassword, 'New password');

      if (newPassword.length < 6) {
        throw new AuthError('Password must be at least 6 characters long');
      }

      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) {
        throw error;
      }
    } catch (error) {
      this.handleError(error, 'AuthService.updatePassword');
    }
  }

  /**
   * Get current session
   */
  async getCurrentSession(): Promise<AuthResponse> {
    try {
      const { data: { session }, error } = await supabase.auth.getSession();

      if (error) {
        throw error;
      }

      return {
        user: session?.user ? {
          id: session.user.id,
          email: session.user.email!,
          name: session.user.user_metadata?.full_name || session.user.email!.split('@')[0],
          user_metadata: session.user.user_metadata,
        } : null,
        session,
      };
    } catch (error) {
      this.handleError(error, 'AuthService.getCurrentSession');
    }
  }

  /**
   * Update user metadata
   */
  async updateUserMetadata(metadata: { full_name?: string }): Promise<AuthUser> {
    try {
      const { data, error } = await supabase.auth.updateUser({
        data: metadata,
      });

      if (error) {
        throw error;
      }

      if (!data.user) {
        throw new AuthError('Failed to update user metadata');
      }

      return {
        id: data.user.id,
        email: data.user.email!,
        name: metadata.full_name || data.user.user_metadata?.full_name || data.user.email!.split('@')[0],
        user_metadata: data.user.user_metadata,
      };
    } catch (error) {
      this.handleError(error, 'AuthService.updateUserMetadata');
    }
  }

  /**
   * Delete user account
   */
  async deleteAccount(): Promise<void> {
    try {
      // Note: Supabase doesn't have a direct delete user method
      // This would typically be handled by a server function
      // For now, we'll sign out the user
      await this.signOut();

      // In a real implementation, you'd call a server function to delete the user
      // const { error } = await supabase.functions.invoke('delete-user');

      throw new AuthError('Account deletion is not yet implemented. Please contact support.');
    } catch (error) {
      this.handleError(error, 'AuthService.deleteAccount');
    }
  }

  /**
   * Create user settings after email verification
   */
  async createUserSettings(userId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('user_settings')
        .insert({
          user_id: userId,
          currency: 'INR',
          locale: 'en-IN',
        });

      if (error) {
        console.warn('Failed to create user settings:', error);
      }
    } catch (error) {
      console.warn('Settings creation error:', error);
    }
  }
}

export const authService = new AuthService();